// store/slices/conversationSlice.js
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  conversations: [],                 // List of conversation summaries
  selectedConversationId: null,     // Currently selected conversation ID
  selectedConversation: null,       // Currently selected conversation details
  messagesByConversation: {},       // Messages grouped by conversation ID
  loading: false,                   // Loading state for async operations
  error: null,                     // Error message or object if any
};

const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    /**
     * Set the entire conversations list in the store.
     * Payload: Array of conversation objects.
     */
    setConversations: (state, action) => {
      state.conversations = action.payload;
    },

    /**
     * Set the selected conversation by its ID.
     * Also updates the selectedConversation object with matching conversation details.
     * Payload: conversation ID (string or number).
     */
    setSelectedConversation: (state, action) => {
      state.selectedConversationId = action.payload;
      state.selectedConversation = state.conversations.find(conv => conv.id === action.payload);
    },

    /**
     * Update the last message of a conversation.
     * Moves updated conversation to the front of the list.
     * If conversation doesn't exist, adds it to the beginning.
     * Payload: { id, message, time, username }
     */
    updateConversationLastMessage: (state, action) => {
      const { id, message, time, username, name, userId } = action.payload;

      const existingConvIndex = state.conversations.findIndex(conv => conv.id === id);

      if (existingConvIndex !== -1) {
        const updatedConv = {
          ...state.conversations[existingConvIndex],
          message,
          date: time
        };

        // Remove old position and add updated conversation to the front
        state.conversations.splice(existingConvIndex, 1);
        state.conversations.unshift(updatedConv);
      } else {
        // Add new conversation if not found
        const newConv = {
          id,
          userName: username,
          name: name,
          message,
          date: time,
          avatar: 'path_to_avatar', // Default or placeholder avatar path
          userId
        };
        state.conversations.unshift(newConv);
      }
    },

    /**
     * Clear currently selected conversation.
     */
    clearSelectedConversation: (state) => {
      state.selectedConversationId = null;
      state.selectedConversation = null;
    },

    /**
     * Replace all messages grouped by conversation.
     * Payload: Object where keys are conversation IDs and values are message arrays.
     */
    setMessagesByConversation: (state, action) => {
      state.messagesByConversation = action.payload;
    },

    /**
     * Add a single message to a specific conversation's messages array.
     * Creates the array if it does not exist.
     * Payload: { conversationId, message }
     */
    addMessageToConversation: (state, action) => {
      const { conversationId, message } = action.payload;

      if (!state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId] = [message];
      } else {
        state.messagesByConversation[conversationId].push(message);
      }
    },

    /**
     * Replace messages for a specific conversation.
     * Payload: { conversationId, messages }
     */
    updateMessagesForConversation: (state, action) => {
      const { conversationId, messages } = action.payload;
      state.messagesByConversation[conversationId] = messages;
    },

    /**
     * Clear messages for a specific conversation by setting empty array.
     * Payload: conversation ID.
     */
    clearMessagesForConversation: (state, action) => {
      const conversationId = action.payload;
      if (state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId] = [];
      }
    },

    /**
     * Add a new conversation to the list.
     * Payload: conversation object.
     */
    addConversation: (state, action) => {
      state.conversations.push(action.payload);
    },

    /**
     * Update an existing conversation fully by ID.
     * Payload: updated conversation object.
     */
    updateConversation: (state, action) => {
      const index = state.conversations.findIndex(conv => conv.id === action.payload.id);
      if (index !== -1) {
        state.conversations[index] = action.payload;
      }
    },

    /**
     * Delete a conversation by ID.
     * Also clears selected conversation if it was deleted.
     * Payload: conversation ID.
     */
    deleteConversation: (state, action) => {
      state.conversations = state.conversations.filter(conv => conv.id !== action.payload);

      if (state.selectedConversationId === action.payload) {
        state.selectedConversationId = null;
        state.selectedConversation = null;
      }
    },

    /**
     * Set loading flag.
     * Payload: boolean.
     */
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    /**
     * Set error message/object.
     * Payload: error.
     */
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const {
  setConversations,
  setSelectedConversation,
  clearSelectedConversation,
  updateConversationLastMessage,
  setMessagesByConversation,
  addMessageToConversation,
  updateMessagesForConversation,
  clearMessagesForConversation,
  addConversation,
  updateConversation,
  deleteConversation,
  setLoading,
  setError,
} = conversationSlice.actions;

export default conversationSlice;